import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "brimi_install_dismissed_at";
const DISMISS_WINDOW_DAYS = 7;

const wasRecentlyDismissed = () => {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    const days = (Date.now() - ts) / 86400000;
    return days < DISMISS_WINDOW_DAYS;
  } catch {
    return false;
  }
};

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

/**
 * Install Brimi PWA banner. Renders only when:
 *   - browser fired beforeinstallprompt (chromium / edge / samsung)
 *   - app isn't already installed
 *   - user hasn't dismissed it in the last 7 days
 */
export const InstallBanner = () => {
  const [ready, setReady] = useState(false);
  const [dismissed, setDismissed] = useState(wasRecentlyDismissed);

  useEffect(() => {
    if (isStandalone()) return undefined;
    const onReady = () => setReady(true);
    const onInstalled = () => setReady(false);
    if (window.__BRIMI_INSTALL_READY__) setReady(true);
    window.addEventListener("brimi-install-ready", onReady);
    window.addEventListener("brimi-installed", onInstalled);
    return () => {
      window.removeEventListener("brimi-install-ready", onReady);
      window.removeEventListener("brimi-installed", onInstalled);
    };
  }, []);

  const onInstall = async () => {
    if (typeof window.__brimiTriggerInstall !== "function") return;
    await window.__brimiTriggerInstall();
    setReady(false);
  };

  const onDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  if (!ready || dismissed || isStandalone()) return null;

  return (
    <div
      data-testid="install-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-50 tactile-card p-4 sm:p-5 shadow-xl border-2 border-[hsl(var(--brimi-blue))]/30 bg-white"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[hsl(var(--brimi-blue))] to-[hsl(var(--brimi-coral))] flex items-center justify-center shrink-0 shadow-sm">
          <Download className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-black text-sm sm:text-base leading-tight">
            Install Brimi AI
          </div>
          <div className="text-xs sm:text-sm text-muted-foreground mt-1">
            One-tap access from your home screen. Works just like an app — no
            browser tabs.
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={onInstall}
              data-testid="install-banner-install-btn"
              className="pill-btn bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white text-xs sm:text-sm px-4 py-1.5"
            >
              <Download className="w-3.5 h-3.5" /> Install
            </button>
            <button
              type="button"
              onClick={onDismiss}
              data-testid="install-banner-later-btn"
              className="text-xs sm:text-sm font-semibold text-muted-foreground hover:text-foreground px-2 py-1.5"
            >
              Maybe later
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          data-testid="install-banner-dismiss"
          aria-label="Dismiss install banner"
          className="p-1 rounded-full hover:bg-foreground/5 -mt-1 -mr-1"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
};
